/**
 * Hata detayı — bir arızanın tam teşhisi.
 *
 * Bu pencerenin cevaplaması gereken sorular, sorulma sırasıyla:
 *
 *   1. Ne oldu?            → başlık + mesaj
 *   2. Hangi fatura?       → fatura kartı (tıklanır)
 *   3. Asıl sebep ne?      → neden zinciri (en dıştan köke)
 *   4. Ne zaman, kaç kez?  → zaman + tekrar sayısı
 *   5. Ne yapmalıyım?      → çözüldü işaretle + not
 *
 * **Neden zinciri neden bu kadar önemli:** dış mesaj genelde işe yaramaz
 * ("Fatura gönderilemedi"). Asıl bilgi zincirin en altındadır
 * ("ECONNREFUSED 443"). O yüzden zincir en dıştan köke doğru çizilir ve
 * **kök vurgulanır** — göz oraya gitsin.
 *
 * Yığın izi ve ham gövde katlanır durur: teknik olmayan kullanıcıyı
 * korkutmasın, teknik olan da arayıp bulmak zorunda kalmasın.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ArrowDown, CheckCircle2, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { Pencere } from './ui/Pencere';
import { Dugme } from './ui/Dugme';
import { Girdi } from './ui/Girdi';
import { DurumRozeti } from './DurumRozeti';
import { Yukleniyor, HataKutusu } from './Durumlar';
import { tarihSaatYaz, goreliZaman } from '../lib/format';
import { TanimSatiri } from './ui/TanimSatiri';

export interface HataDetayPenceresiProps {
  id: string;
  onKapat: () => void;
  /** `hata.duzenle` yetkisi var mı? Yoksa kapatma bölümü hiç çizilmez. */
  cozebilir: boolean;
  cozumleniyor: boolean;
  onCoz: (girdi: { not?: string; tumGrup: boolean }) => void;
}

export function HataDetayPenceresi({ id, onKapat, cozebilir, cozumleniyor, onCoz }: HataDetayPenceresiProps) {
  const [not, setNot] = useState('');
  const [tumGrup, setTumGrup] = useState(false);

  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['hata-detay', id],
    queryFn: () => api.hataDetayi(id),
  });

  return (
    <Pencere
      acik
      genis
      baslik={data?.cinsEtiketi ?? 'Hata kaydı'}
      aciklama={data ? `${data.alanEtiketi} · ${data.islem}` : 'Yükleniyor…'}
      basliksag={
        data ? (
          <DurumRozeti
            ton={data.seviye === 'CRITICAL' ? 'danger' : data.seviye === 'ERROR' ? 'warning' : 'info'}
            etiket={data.seviyeEtiketi}
          />
        ) : undefined
      }
      onKapat={onKapat}
      altCubuk={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Dugme gorunum="sessiz" onClick={onKapat}>
            Kapat
          </Dugme>
          {cozebilir && data && !data.cozuldu && (
            <Dugme
              gorunum="birincil"
              ikon={<CheckCircle2 size={15} />}
              bekliyor={cozumleniyor}
              onClick={() => onCoz({ tumGrup, ...(not.trim() ? { not: not.trim() } : {}) })}
            >
              {tumGrup && data.tekrar > 1 ? `${data.tekrar} kaydı birden kapat` : 'Çözüldü işaretle'}
            </Dugme>
          )}
        </div>
      }
    >
      {isPending && <Yukleniyor satir={4} />}
      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}

      {data && (
        <div className="flex flex-col gap-6">
          {/* ---- 1. Ne oldu ---- */}
          <section className="flex flex-col gap-2">
            <p className="text-sm leading-relaxed text-[var(--ink)]">{data.mesaj}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--ink-faint)]">
              <span className="rakam">{tarihSaatYaz(data.tarih)}</span>
              <span>{goreliZaman(data.tarih)}</span>
              {data.tekrar > 1 && (
                <span className="text-[var(--state-bad)]">Bu arıza toplam {data.tekrar} kez oldu</span>
              )}
              {data.deneme !== null && <span>{data.deneme}. deneme</span>}
              <span>{data.tekrarDenenecek ? 'Otomatik tekrar denenecek' : 'Otomatik denenmeyecek'}</span>
            </div>
          </section>

          {/* ---- 2. Hangi fatura ---- */}
          {data.fatura !== null ? (
            <section className="flex flex-col gap-2">
              <h3 className="ust-etiket">Etkilenen fatura</h3>
              <Link
                to={`/faturalar/${encodeURIComponent(data.fatura.kod)}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--line-strong)] p-4 hover:bg-[var(--surface-hover)]"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="rakam text-sm font-medium text-[var(--ink)]">
                    {data.fatura.numara ?? 'Numara henüz alınmadı'}
                  </span>
                  <span className="rakam truncate text-xs text-[var(--ink-faint)]">{data.fatura.kod}</span>
                  {data.fatura.alici !== null && (
                    <span className="truncate text-xs text-[var(--ink-muted)]">{data.fatura.alici}</span>
                  )}
                </div>
                <ExternalLink size={16} className="shrink-0 text-[var(--ink-faint)]" />
              </Link>
            </section>
          ) : (
            <section className="flex flex-col gap-1">
              <h3 className="ust-etiket">Etkilenen fatura</h3>
              <p className="text-sm text-[var(--ink-muted)]">
                Bu hata bir faturaya bağlanamadı
                {data.referans !== null ? (
                  <>
                    {' — '}
                    <span className="rakam">{data.referans}</span> için fatura kaydı henüz oluşmamış.
                  </>
                ) : (
                  '. Faturadan önceki bir aşamada oluştu.'
                )}
              </p>
            </section>
          )}

          {/* ---- 3. Asıl sebep ---- */}
          {Array.isArray(data.nedenZinciri) && data.nedenZinciri.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="ust-etiket">Asıl sebep zinciri</h3>
              <ol className="flex flex-col gap-1">
                {data.nedenZinciri.map((halka, sira) => {
                  const kok = sira === data.nedenZinciri!.length - 1;
                  return (
                    <li key={`${halka.ad}-${sira}`} className="flex flex-col gap-1">
                      {sira > 0 && (
                        <ArrowDown size={13} className="ml-3 text-[var(--ink-faint)]" aria-hidden="true" />
                      )}
                      <div
                        className={
                          kok
                            ? 'rounded-[var(--radius-md)] border border-[var(--state-bad)] bg-[var(--state-bad-soft)] p-3'
                            : 'rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3'
                        }
                      >
                        <span className="rakam block text-xs text-[var(--ink-faint)]">
                          {halka.ad}
                          {kok && ' · asıl sebep'}
                        </span>
                        <span className="block text-sm text-[var(--ink)]">{halka.mesaj}</span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {/* ---- Teknik künye ---- */}
          <section className="flex flex-col gap-2">
            <h3 className="ust-etiket">Teknik künye</h3>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 @xl:grid-cols-2">
              <TanimSatiri etiket="Hata cinsi" deger={data.cins} />
              {data.kod !== null && <TanimSatiri etiket="Hata kodu" deger={data.kod} />}
              {data.httpKodu !== null && <TanimSatiri etiket="HTTP kodu" deger={String(data.httpKodu)} />}
              {data.isTipi !== null && <TanimSatiri etiket="Arka plan işi" deger={data.isTipi} />}
              <TanimSatiri etiket="Bölüm" deger={data.alanEtiketi} />
              <TanimSatiri etiket="İşlem" deger={data.islem} />
            </dl>
          </section>

          {/* ---- Ayrıntı ve yığın: katlı ---- */}
          {data.ayrinti !== null && Object.keys(data.ayrinti).length > 0 && (
            <Katli baslik="Çağrı ayrıntısı">
              <pre className="overflow-x-auto rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 text-xs whitespace-pre-wrap text-[var(--ink-muted)]">
                {JSON.stringify(data.ayrinti, null, 2)}
              </pre>
            </Katli>
          )}

          {data.yigin !== null && data.yigin !== '' && (
            <Katli baslik="Yığın izi (geliştirici için)">
              <pre className="overflow-x-auto rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 text-xs whitespace-pre-wrap text-[var(--ink-muted)]">
                {data.yigin}
              </pre>
            </Katli>
          )}

          {/* ---- Aynı faturanın diğer hataları ---- */}
          {data.digerHatalar.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="ust-etiket">Aynı faturanın diğer hataları ({data.digerHatalar.length})</h3>
              <ul className="flex flex-col gap-1">
                {data.digerHatalar.map((h) => (
                  <li
                    key={h.id}
                    className="flex flex-wrap items-baseline justify-between gap-2 rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-3 py-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-xs text-[var(--ink-muted)]">{h.mesaj}</span>
                    <span className="rakam shrink-0 text-xs text-[var(--ink-faint)]">
                      {tarihSaatYaz(h.tarih)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ---- 5. Ne yapmalıyım ---- */}
          {data.cozuldu ? (
            <section className="flex flex-col gap-1 rounded-[var(--radius-md)] bg-[var(--state-ok-soft)] p-4">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--state-ok)]">
                <CheckCircle2 size={15} aria-hidden="true" />
                {data.cozen ?? 'Bir kullanıcı'} tarafından kapatıldı
              </span>
              {data.cozumTarihi !== null && (
                <span className="rakam text-xs text-[var(--ink-muted)]">
                  {tarihSaatYaz(data.cozumTarihi)}
                </span>
              )}
              {data.cozumNotu !== null && data.cozumNotu !== '' && (
                <p className="text-sm text-[var(--ink)]">{data.cozumNotu}</p>
              )}
            </section>
          ) : (
            cozebilir && (
              <section className="flex flex-col gap-3 border-t border-[var(--line)] pt-4">
                <Girdi
                  etiket="Çözüm notu"
                  ipucu="Üç ay sonra aynı arıza olduğunda “geçen sefer ne yapmıştık” sorusunun cevabı bu olacak."
                  value={not}
                  onChange={(olay) => setNot(olay.target.value)}
                  placeholder="Örn: Shopify erişim anahtarı yenilendi"
                />
                {data.tekrar > 1 && (
                  <label className="flex items-start gap-2 text-sm text-[var(--ink)]">
                    <input
                      type="checkbox"
                      checked={tumGrup}
                      onChange={(olay) => setTumGrup(olay.target.checked)}
                      className="mt-0.5 size-4 shrink-0 accent-[var(--accent)]"
                    />
                    <span>
                      Aynı arızanın açık olan {data.tekrar} kaydını birden kapat
                      <span className="block text-xs text-[var(--ink-faint)]">
                        Daha önce başkası tarafından kapatılmış kayıtların notu değişmez.
                      </span>
                    </span>
                  </label>
                )}
              </section>
            )
          )}
        </div>
      )}
    </Pencere>
  );
}

/**
 * Katlanır bölüm.
 *
 * `<details>` kullanılıyor: klavye ve ekran okuyucu desteği tarayıcıdan
 * geliyor, kendi açılır-kapanır mekanizmamızı yazmaya gerek yok.
 */
function Katli({ baslik, children }: { baslik: string; children: React.ReactNode }) {
  return (
    <details className="group">
      <summary className="ust-etiket cursor-pointer list-none select-none hover:text-[var(--ink)]">
        {baslik}
        <span className="ml-1 text-[var(--ink-faint)] group-open:hidden">▸</span>
        <span className="ml-1 hidden text-[var(--ink-faint)] group-open:inline">▾</span>
      </summary>
      <div className="mt-2">{children}</div>
    </details>
  );
}
